import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Form, Pagination, Row, Col, Button, Modal } from 'react-bootstrap';
import { Link, useHistory, useLocation } from 'react-router-dom';
import api from '../utils/api';
import useAuth from '../context/useAuth';

const CardPayments = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [sortOrder, setSortOrder] = useState('DESC');
    const [receiptstatus, setReceiptstatus] = useState('');
    const [dateRange, setDateRange] = useState('all');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');

    /* ===== Manual Match State (ADDED) ===== */
    const [showMatchModal, setShowMatchModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [manualMemberId, setManualMemberId] = useState('');
    const [manualReceiptNum, setManualReceiptNum] = useState('');
    const [saving, setSaving] = useState(false);

    const history = useHistory();
    const location = useLocation();
    const { resultsPerPage } = useAuth();
    const pageSize = parseInt(resultsPerPage, 10) || 25;

    const CARD_PAYMENT_STATUSES = [
        'PENDING_MATCH',
        'MATCHED_BY_EMAIL',
        'MATCHED_BY_PHONE',
        'MATCHED_MANUALLY',
        'MULTIPLE_MATCHES',
        'NO_MATCH_FOUND'
    ];

    const hasUserChangedDateRange = useRef(false);

    const getCardPayments = useCallback(() => {
        setLoading(true);

        let path =
            `/api/card_payments?page=${currentPage}` +
            `&limit=${pageSize}` +
            `&sortOrder=${sortOrder}` +
            (receiptstatus ? `&receiptstatus=${receiptstatus}` : '') +
            `&dateRange=${dateRange}`;

        if (dateRange === 'custom') {
            path += `&startDate=${customStartDate}&endDate=${customEndDate}`;
        }

        api.get(path)
            .then(res => {
                const { data, totalPages } = res.data;
                setPayments(data);
                setTotalPages(totalPages || 1);
                setLoading(false);
            })
            .catch(err => {
                if (err.response && (err.response.status === 401 || err.response.status === 403)) {
                    history.push('/login');
                } else {
                    setError(err.message);
                    setLoading(false);
                }
            });
    }, [
        currentPage,
        pageSize,
        sortOrder,
        receiptstatus,
        dateRange,
        customStartDate,
        customEndDate,
        history
    ]);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const pageNumber = parseInt(queryParams.get('page'), 10);
        if (!isNaN(pageNumber) && pageNumber !== currentPage) {
            setCurrentPage(pageNumber);
        }
    }, [location.search, currentPage]);

    useEffect(() => {
        getCardPayments();
    }, [getCardPayments]);

    const handlePageChange = (pageNumber) => {
        if (pageNumber !== currentPage) {
            setCurrentPage(pageNumber);
            history.push(`?page=${pageNumber}`);
        }
    };

    const handleDateRangeChange = (event) => {
        setDateRange(event.target.value);
        hasUserChangedDateRange.current = true;
        setCurrentPage(1);
        history.push('?page=1');
    };

    const handleCustomDateChange = (e) => {
        if (e.target.name === 'customStartDate') setCustomStartDate(e.target.value);
        else if (e.target.name === 'customEndDate') setCustomEndDate(e.target.value);
    };

    const handleCustomDateSubmit = () => {
        setCurrentPage(1);
        getCardPayments();
    };

    const handleSortOrderChange = (event) => {
        setSortOrder(event.target.value);
        setCurrentPage(1);
        history.push('?page=1');
    };

    /* ===== Manual Match Handlers (ADDED) ===== */
    const openManualMatchModal = (payment) => {
        setSelectedPayment(payment);
        setManualMemberId(payment.memberId || '');
        setManualReceiptNum(payment.receiptNum || '');
        setShowMatchModal(true);
    };

    const closeManualMatchModal = () => {
        setShowMatchModal(false);
        setSelectedPayment(null);
        setManualMemberId('');
        setManualReceiptNum('');
    };

    const handleManualMatchSave = async () => {
        if (!manualMemberId || !manualReceiptNum) {
            alert('Member ID and Receipt # are required');
            return;
        }

        setSaving(true);
        try {
            await api.put(
                `/api/card_payments/${selectedPayment.id}/manual-match`,
                { memberId: manualMemberId, receiptNum: manualReceiptNum }
            );
            closeManualMatchModal();
            getCardPayments();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    const rows = payments.map(payment => (
        <tr key={payment.id}>
            <td className="align-middle">
                {payment.transactionId ? (
                    <Link to={`/cardPaymentEmail/${payment.id}`} className="receipt-link">
                        {payment.transactionId}
                    </Link>
                ) : '-'}
            </td>
            <td>{payment.paymentDate}</td>
            <td>{payment.approvalCode}</td>
            <td>{payment.customerName}</td>
            <td>{payment.customerPhone}</td>
            <td>{payment.customerEmail}</td>
            <td className="text-right">
                ${parseFloat(payment.amount || 0).toFixed(2)}
            </td>
            <td>{payment.receiptstatus}</td>
            <td className="align-middle">
                {payment.memberId ? (
                    <Link
                        to={{
                            pathname: `/memberDetails/${payment.memberId}`,
                            state: { fromCardPayments: true, page: currentPage }
                        }}
                    >
                        {payment.memberId}
                    </Link>
                ) : '-'}
            </td>
            <td className="align-middle">
                {payment.receiptNum ? (
                    <Link
                        to={{
                            pathname: `/receiptDetails/${payment.receiptNum}`,
                            state: { fromCardPayments: true, page: currentPage }
                        }}
                    >
                        {payment.receiptNum}
                    </Link>
                ) : '-'}
            </td>
            <td className="text-center">
                {payment.receiptstatus !== 'MATCHED_BY_EMAIL' &&
                 payment.receiptstatus !== 'MATCHED_BY_PHONE' && (
                    <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => openManualMatchModal(payment)}
                    >
                        Match
                    </Button>
                )}
            </td>
        </tr>
    ));

    const maxPageNumbersToShow = 10;
    const currentGroup = Math.ceil(currentPage / maxPageNumbersToShow);
    const startPage = (currentGroup - 1) * maxPageNumbersToShow + 1;
    const endPage = Math.min(startPage + maxPageNumbersToShow - 1, totalPages);

    return (
        <div>
            {/* ===== FILTERS ===== */}
            <Row className="mb-2 no-print">
                <Col md={3}>
                    <Form.Label className="small-font font-weight-bold">Date Range</Form.Label>
                    <Form.Control
                        as="select"
                        value={dateRange}
                        onChange={handleDateRangeChange}
                        className="small-font"
                    >
                        <option value="all">All</option>
                        <option value="currentYear">Current Year</option>
                        <option value="currentMonth">Current Month</option>
                        <option value="currentWeek">Current Week</option>
                        <option value="currentDay">Current Day</option>
                        <option value="lastYear">Last Year</option>
                        <option value="lastMonth">Last Month</option>
                        <option value="lastWeek">Last Week</option>
                        <option value="lastDay">Last Day</option>
                        <option value="custom">Custom</option>
                    </Form.Control>
                </Col>

                {dateRange === 'custom' && (
                    <>
                        <Col md={3}>
                            <Form.Label className="small-font font-weight-bold">Start Date</Form.Label>
                            <Form.Control
                                type="date"
                                name="customStartDate"
                                value={customStartDate}
                                onChange={handleCustomDateChange}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="small-font font-weight-bold">End Date</Form.Label>
                            <Form.Control
                                type="date"
                                name="customEndDate"
                                value={customEndDate}
                                onChange={handleCustomDateChange}
                            />
                        </Col>
                        <Col md={3} className="d-flex align-items-end">
                            <Button onClick={handleCustomDateSubmit}>Apply</Button>
                        </Col>
                    </>
                )}

                <Col md={3} className="mt-2">
                    <Form.Label className="small-font font-weight-bold">
                        Sort by Payment Date
                    </Form.Label>
                    <div className="radio-box p-2">
                        <Form.Check
                            type="radio"
                            name="sortOrder"
                            value="DESC"
                            label="Descending"
                            checked={sortOrder === 'DESC'}
                            onChange={handleSortOrderChange}
                            inline
                        />
                        <Form.Check
                            type="radio"
                            name="sortOrder"
                            value="ASC"
                            label="Ascending"
                            checked={sortOrder === 'ASC'}
                            onChange={handleSortOrderChange}
                            inline
                        />
                    </div>
                </Col>

                <Col md={3} className="mt-2">
                    <Form.Label className="small-font font-weight-bold">Status</Form.Label>
                    <Form.Control
                        as="select"
                        value={receiptstatus}
                        onChange={(e) => {
                            setReceiptstatus(e.target.value);
                            setCurrentPage(1);
                            history.push('?page=1');
                        }}
                    >
                        <option value="">All</option>
                        {CARD_PAYMENT_STATUSES.map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </Form.Control>
                </Col>
            </Row>

            {/* ===== TABLE ===== */}
            <Table striped bordered hover size="sm" className="small-font">
                <thead>
                    <tr>
                        <th>Transaction ID</th>
                        <th>Payment Date</th>
                        <th>Approval Code</th>
                        <th>Bill To</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th className="text-right">Amount</th>
                        <th>Status</th>
                        <th>Member ID</th>
                        <th>Receipt #</th>
                        <th>Manual Match</th>
                    </tr>
                </thead>
                <tbody>
                    {payments.length === 0 ? (
                        <tr>
                            <td colSpan="12" className="text-center">
                                No card payments found
                            </td>
                        </tr>
                    ) : rows}
                </tbody>
            </Table>

            {/* ===== PAGINATION ===== */}
            <Pagination>
                <Pagination.First
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                />
                <Pagination.Prev
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                />

                {startPage > 1 && (
                    <Pagination.Ellipsis
                        onClick={() => handlePageChange(startPage - 1)}
                    />
                )}

                {[...Array(endPage - startPage + 1)].map((_, index) => {
                    const pageNumber = startPage + index;
                    return (
                        <Pagination.Item
                            key={pageNumber}
                            active={pageNumber === currentPage}
                            onClick={() => handlePageChange(pageNumber)}
                        >
                            {pageNumber}
                        </Pagination.Item>
                    );
                })}

                {endPage < totalPages && (
                    <Pagination.Ellipsis
                        onClick={() => handlePageChange(endPage + 1)}
                    />
                )}

                <Pagination.Next
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                />
                <Pagination.Last
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                />
            </Pagination>

            {/* ===== MANUAL MATCH MODAL ===== */}
            <Modal show={showMatchModal} onHide={closeManualMatchModal} centered size="sm">
                <Modal.Header closeButton>
                    <Modal.Title>Manual Match</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Member ID</Form.Label>
                        <Form.Control
                            value={manualMemberId}
                            onChange={(e) => setManualMemberId(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mt-2">
                        <Form.Label>Receipt #</Form.Label>
                        <Form.Control
                            value={manualReceiptNum}
                            onChange={(e) => setManualReceiptNum(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeManualMatchModal}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleManualMatchSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default CardPayments;